export type DayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface DayHours {
  open: string;
  close: string;
}

export type OpeningHoursValue = Record<DayKey, DayHours | null>;

export const DAY_KEYS: DayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const DAY_LABELS: Record<DayKey, string> = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
};

export function emptyOpeningHours(): OpeningHoursValue {
  return {
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
    sunday: null,
  };
}

export function normalizeOpeningHours(raw: unknown): OpeningHoursValue {
  const base = emptyOpeningHours();
  if (!raw || typeof raw !== 'object') return base;

  const obj = raw as Record<string, unknown>;
  DAY_KEYS.forEach((key) => {
    const val = obj[key];
    if (val && typeof val === 'object') {
      const d = val as { open?: unknown; close?: unknown };
      const open = typeof d.open === 'string' ? d.open : '';
      const close = typeof d.close === 'string' ? d.close : '';
      if (open || close) {
        base[key] = { open, close };
      }
    }
  });

  return base;
}

interface OpeningHoursEditorProps {
  value: OpeningHoursValue;
  onChange: (value: OpeningHoursValue) => void;
}

function OpeningHoursEditor({ value, onChange }: OpeningHoursEditorProps) {
  const updateDay = (key: DayKey, patch: Partial<DayHours> | null) => {
    const next = { ...value };
    if (patch === null) {
      next[key] = null;
    } else {
      const current = next[key] ?? { open: '', close: '' };
      next[key] = { ...current, ...patch };
    }
    onChange(next);
  };

  const copyToAll = (from: DayKey) => {
    const source = value[from];
    if (!source) return;
    const next = { ...value };
    DAY_KEYS.forEach((key) => {
      next[key] = { ...source };
    });
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {DAY_KEYS.map((key) => {
        const slot = value[key];
        const isOpen = slot !== null;
        return (
          <div
            key={key}
            className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg bg-background-100/60 border border-background-200/70"
          >
            <div className="flex items-center gap-3 sm:w-44 shrink-0">
              <button
                type="button"
                onClick={() => updateDay(key, isOpen ? null : { open: '08:00', close: '18:00' })}
                className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${
                  isOpen ? 'bg-primary-500' : 'bg-background-300'
                }`}
                aria-label={`${isOpen ? 'Fermer' : 'Ouvrir'} ${DAY_LABELS[key]}`}
              >
                <span
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-foreground-50 transition-all ${
                    isOpen ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-foreground-50 whitespace-nowrap">
                {DAY_LABELS[key]}
              </span>
            </div>

            {isOpen ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="time"
                  value={slot.open}
                  onChange={(e) => updateDay(key, { open: e.target.value })}
                  className="bg-background-100 border border-background-200/70 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400/50"
                />
                <span className="text-foreground-400 text-sm">à</span>
                <input
                  type="time"
                  value={slot.close}
                  onChange={(e) => updateDay(key, { close: e.target.value })}
                  className="bg-background-100 border border-background-200/70 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400/50"
                />
                {key === 'monday' && (
                  <button
                    type="button"
                    onClick={() => copyToAll('monday')}
                    className="ml-auto text-xs text-primary-400 hover:text-primary-300 font-medium whitespace-nowrap"
                  >
                    Appliquer à toute la semaine
                  </button>
                )}
              </div>
            ) : (
              <span className="text-sm text-foreground-400">Fermé</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default OpeningHoursEditor;